// src/components/HeroVideoSlider.tsx
import React, { useEffect, useRef, useState } from "react";

export type VideoSlide = {
  id?: string;
  title: string;
  subtitle?: string;
  videoSrc?: string; // e.g. "/videos/hero1.mp4" or imported module
  poster?: string;   // e.g. "/images/hero1.jpg"
};

type Props = {
  slides: VideoSlide[];
  intervalMs?: number;
  heroMaxWidth?: string;
  mobileBreakpoint?: number;
};

const HeroVideoSlider: React.FC<Props> = ({
  slides,
  intervalMs = 6000,
  heroMaxWidth = "min(760px,86%)",
  mobileBreakpoint = 768,
}) => {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [playBlocked, setPlayBlocked] = useState(false); // true when autoplay blocked
  const [isMobile, setIsMobile] = useState(false);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  // update mobile flag
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < mobileBreakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [mobileBreakpoint]);

  // auto-advance
  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(id);
  }, [isPaused, slides.length, intervalMs]);

  // Try to play/pause videos, detect blocked autoplay
  useEffect(() => {
    let blocked = false;
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      // ensure inline attributes for mobile browsers
      try {
        v.setAttribute("playsinline", "true");
        v.setAttribute("webkit-playsinline", "true");
      } catch {}
      if (i === index) {
        v.currentTime = 0;
        // attempt to play
        const p = v.play();
        if (p && typeof p.then === "function") {
          p
            .then(() => {
              // playback started
              setPlayBlocked(false);
            })
            .catch(() => {
              // autoplay blocked
              blocked = true;
              setPlayBlocked(true);
            });
        }
      } else {
        try { v.pause(); } catch {}
      }
    });

    // if blocked variable set to true, setPlayBlocked (safe update)
    if (blocked) setPlayBlocked(true);

    // cleanup not needed
  }, [index, isMobile]);

  // keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % slides.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + slides.length) % slides.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  const goTo = (i: number) => setIndex((i + slides.length) % slides.length);
  const prev = () => goTo(index - 1);
  const next = () => goTo(index + 1);

  const onUserPlay = async () => {
    // user tapped the play button => try to play current video
    const v = videoRefs.current[index];
    if (!v) return;
    try {
      await v.play();
      setPlayBlocked(false);
    } catch {
      // still blocked or error — keep showing button
      setPlayBlocked(true);
    }
  };

  const current = slides[index];

  return (
    <section
      id="home"
      className="relative flex items-center justify-center min-h-[70vh] md:min-h-[85vh] text-white overflow-hidden"
      style={{ paddingTop: "90px" /* adjust for your fixed header height */ }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background video elements */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        {slides.map((s, i) => {
          const visible = i === index;
          return (
            <video
              key={s.id ?? i}
              ref={(el) => (videoRefs.current[i] = el)}
              muted
              loop
              playsInline
              // give iOS support; TSX may not type this prop, so set attribute in effect as well
              // @ts-ignore
              webkit-playsinline="true"
              preload="metadata"
              poster={s.poster}
              src={s.videoSrc}
              className={`absolute left-1/2 top-1/2 min-w-[100%] min-h-[100%] w-auto h-auto transform -translate-x-1/2 -translate-y-1/2 object-cover transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              controls={false}
            />
          );
        })}

        {/* universal dark tint so text is readable */}
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />
      </div>

      {/* Content (render only current slide headline so it cannot overlap) */}
      <div className="container mx-auto px-6 relative z-20 flex flex-col items-center text-center justify-center" style={{ minHeight: "50vh" }}>
        <div style={{ maxWidth: heroMaxWidth }} className="px-4">
          <h1
            className="font-black text-3xl sm:text-4xl md:text-5xl lg:text-5xl leading-tight text-white"
            style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", lineHeight: 1.04, margin: 0 }}
          >
            {current.title}
          </h1>

          {current.subtitle && (
            <p className="mt-4 text-base md:text-lg text-gray-200 font-light max-w-3xl mx-auto">
              {current.subtitle}
            </p>
          )}
        </div>

        {/* CTA buttons */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <div className="flex gap-4">
            <a href="#impact" title="Learn about our impact" className="bg-green-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-green-500 transition">Learn More</a>
            <a href="#contact" className="bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg hover:bg-gray-600 transition">Connect with Us</a>
          </div>

          {/* controls and dots */}
          <div className="mt-6 flex items-center gap-3">
            <button onClick={prev} aria-label="Previous" className="p-2 rounded-full bg-black bg-opacity-30 hover:bg-opacity-60 transition text-white">‹</button>

            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`w-3 h-3 rounded-full transition-transform ${i === index ? "scale-110 bg-white" : "bg-white/40"}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button onClick={next} aria-label="Next" className="p-2 rounded-full bg-black bg-opacity-30 hover:bg-opacity-60 transition text-white">›</button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroVideoSlider;
